import type { ReactNode } from 'react';
import { Card } from './Card';

interface EmptyStateProps {
  /** Lucide icon component (or any ReactNode rendered in the icon slot). */
  icon: ReactNode;
  /** Primary heading. */
  title: string;
  /** Secondary explanatory text. */
  description: string;
  /** Optional CTA button(s). */
  action?: ReactNode;
}

/**
 * Friendly empty state for any "nothing here yet" screen. Used across:
 *   - No saved builds
 *   - No recommendation results
 *   - No setup selected (recovery edge case)
 *   - No active arrow (recovery edge case)
 *   - No marks in current sight tape range
 *
 * Polished pattern: rounded icon, warm muted copy, optional CTA. Consistent
 * across the app so users learn "this is what 'not yet' looks like."
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <Card padding="lg" className="text-center py-12 sm:py-16">
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 rounded-2xl bg-ember-500/10 border border-ember-500/20 flex items-center justify-center text-ember-500">
          {icon}
        </div>
      </div>
      <h3 className="font-semibold text-zinc-100 mb-2 text-base">{title}</h3>
      <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
        {description}
      </p>
      {action && <div className="mt-5">{action}</div>}
    </Card>
  );
}
